
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Product } from "@/lib/types";
import { fetchProductsFromApi } from "@/lib/storage/product/api";
import { logError } from "@/utils/errorLogger";

export const useProductData = (productId: string | undefined) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<"ostomy" | "medicine" | "other">("ostomy");
  const [currentStock, setCurrentStock] = useState(0);
  const [usageRate, setUsageRate] = useState(1);
  const [minStockAlert, setMinStockAlert] = useState(7);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [packageSize, setPackageSize] = useState<number>(0);
  const [supplierName, setSupplierName] = useState("");
  const [supplierWebsite, setSupplierWebsite] = useState("");
  const [supplierContact, setSupplierContact] = useState("");
  const [productCode, setProductCode] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  
  const navigate = useNavigate();
  const { toast } = useToast();

  // Load product data
  useEffect(() => {
    const loadProduct = async () => {
      setIsLoading(true);
      
      if (!productId) {
        console.log("No productId provided, redirecting to products page");
        navigate("/products");
        return;
      }

      try {
        console.log("Loading product with ID:", productId);
        
        // First try to fetch products from API (which includes remote data)
        const products = await fetchProductsFromApi();
        console.log(`Found ${products.length} products in total`);
        
        const foundProduct = products.find(p => p.id === productId);
        
        if (!foundProduct) {
          console.log(`Product with ID ${productId} not found in API results, redirecting to products`);
          toast({
            title: "Product not found",
            description: "The product you're trying to edit doesn't exist",
            variant: "destructive",
          });
          navigate("/products");
          return;
        }

        console.log("Found product:", foundProduct);
        setProduct(foundProduct);
        setName(foundProduct.name);
        setDescription(foundProduct.description || "");
        setCategory(foundProduct.category as "ostomy" | "medicine" | "other");
        setCurrentStock(foundProduct.currentStock);
        setUsageRate(foundProduct.usageRate);
        setMinStockAlert(foundProduct.minStockAlert);
        setSelectedGroups(foundProduct.groupIds || []);
        setPackageSize(foundProduct.packageSize || 0);
        setSupplierName(foundProduct.supplierName || "");
        setSupplierWebsite(foundProduct.supplierWebsite || "");
        setSupplierContact(foundProduct.supplierContact || "");
        setProductCode(foundProduct.productCode || "");
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading product:", error);
        toast({
          title: "Error loading product",
          description: "There was an error loading the product data",
          variant: "destructive",
        });
        logError({
          type: "api",
          message: "Failed to load product data",
          stack: error instanceof Error ? error.stack : undefined
        });
        navigate("/products");
      }
    };
    
    loadProduct();
  }, [productId, navigate, toast]);

  return {
    product,
    isLoading,
    name,
    setName,
    description,
    setDescription,
    category,
    setCategory,
    currentStock,
    setCurrentStock,
    usageRate,
    setUsageRate,
    minStockAlert,
    setMinStockAlert,
    selectedGroups,
    setSelectedGroups,
    packageSize,
    setPackageSize,
    supplierName,
    setSupplierName,
    supplierWebsite,
    setSupplierWebsite,
    supplierContact,
    setSupplierContact,
    productCode,
    setProductCode
  };
};
